import {
  Backend,
  getQueryParamValue,
  OrderByDirection,
} from '@freelancer/datastore/core';
import { EntryStatusApi } from 'api-typings/contests/contests';
import { EntryAction } from './contest-view-entries.backend-model';

// This config is being reused on `contestQuickviewEntries` backend
export const contestViewEntriesBackendConfig: Backend<
  'contestViewEntries' | 'contestQuickviewEntries'
> = {
  defaultOrder: [
    {
      field: 'statusNumber',
      direction: OrderByDirection.DESC,
    },
    {
      field: 'rating',
      direction: OrderByDirection.DESC,
    },
    {
      field: 'number',
      direction: OrderByDirection.DESC,
    },
  ],

  fetch: (_, entryIds, query, order) => {
    let fromRating;
    let toRating;

    if (
      query &&
      query.queryParams &&
      query.queryParams.rating &&
      query.queryParams.rating[0]
    ) {
      const ratingQuery = query.queryParams.rating[0];

      if (ratingQuery.condition === '>=') {
        fromRating = ratingQuery.value;
      } else if (ratingQuery.condition === '<') {
        toRating = ratingQuery.value;
      }
    }

    const requestParams: {
      [k: string]:
        | string
        | number
        | readonly string[]
        | readonly number[]
        | undefined;
    } = {
      entries: getQueryParamValue(query, 'id') || entryIds || [],
      contests: getQueryParamValue(query, 'contestId'),
      statuses: getQueryParamValue(query, 'status'),
      owners: getQueryParamValue(query, 'contestOwnerId'),
      entrants: getQueryParamValue(query, 'ownerId'),
      from_rating: fromRating,
      to_rating: toRating,
      file_details: 'true',
      comment_count: 'true',
    };

    if (order && order[0]) {
      const apiSort = transformEntrySortToApiSort(order[0].field);
      if (apiSort) {
        requestParams.sort_field = apiSort;
      }

      if (order[0].direction === OrderByDirection.ASC) {
        requestParams.sort_asc = 'true';
      }
    }

    return {
      endpoint: `contests/0.1/entries`,
      params: requestParams,
    };
  },

  update: (authUid, entry, originalEntry) => {
    let payload;

    if (entry.rating) {
      payload = {
        action: EntryAction.RATE,
        rating: entry.rating,
      };
    } else if (
      entry.isLiked !== undefined &&
      entry.isLiked !== originalEntry.isLiked
    ) {
      payload = {
        action: entry.isLiked ? EntryAction.LIKE : EntryAction.DISLIKE,
      };
    } else if (entry.status && entry.status !== originalEntry.status) {
      let action;

      switch (entry.status) {
        case EntryStatusApi.WON:
          action = EntryAction.AWARD;
          break;
        case EntryStatusApi.BOUGHT:
          action = EntryAction.BUY;
          break;
        case EntryStatusApi.ELIMINATED:
          action = EntryAction.REJECT;
          break;
        case EntryStatusApi.WITHDRAWN_ELIMINATED:
          if (originalEntry.status === EntryStatusApi.WITHDRAWN) {
            action = EntryAction.REJECT;
          } else {
            // TODO: We're doing employer side actions for now
            // add support for action = `withdraw` after T78554 is implemented
            throw new Error(`
              Unknown action for entry status transition from ${entry.status}
              to ${EntryStatusApi.WITHDRAWN_ELIMINATED}
            `);
          }
          break;
        case EntryStatusApi.ACTIVE:
          if (originalEntry.status === EntryStatusApi.ELIMINATED) {
            action = EntryAction.RECONSIDER;
          } else {
            throw new Error(`
              Unknown action for entry status transition from
              ${originalEntry.status} to ${EntryStatusApi.ACTIVE}
            `);
          }
          break;
        default:
          throw new Error(`Unknown action for status: ${entry.status}`);
      }

      payload = { action };
    }

    if (!payload || !payload.action) {
      throw new Error(`Missing payload/action in entry update: ${payload}`);
    }

    return {
      endpoint: `/contests/0.1/entries/${originalEntry.id}/`,
      method: 'PUT',
      payload,
    };
  },
};

export function contestViewEntriesBackend(): Backend<'contestViewEntries'> {
  // FIXME: cast is necessary because Backend<'contestQuickviewEntries'> and Backend<'contestViewEntries'>
  // are seen as different types due to the different type param, even if they're functionally the same
  return contestViewEntriesBackendConfig as Backend<'contestViewEntries'>;
}

function transformEntrySortToApiSort(entrySort: string): string | null {
  switch (entrySort) {
    case 'rating':
      return 'RATING';
    case 'number':
      return 'NUMBER';
    case 'likeCount':
      return 'NUM_LIKES';
    default:
      return null;
  }
}
